/**
 * `POST /admin/users/{user_id}/password` — two callers, one route (§4.3).
 *
 * SELF: `current_password` is required.
 * ADMIN RESETTING ANOTHER: `current_password` must be ABSENT — sending it would
 * be a 422, and asking an admin for someone else's current password is nonsense.
 *
 * Either way every session that user holds is revoked, including the caller's own
 * when this is a self change (§5, "forced logout-all on password change") — which
 * is why the self path signs the panel out afterwards rather than pretending the
 * session survived.
 */
import { useState, type FormEvent } from 'react';
import { api, errorKey, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { Button, ErrorBanner, Field, Modal, TextInput, WarningBanner } from '../ui/index.jsx';

export function PasswordDialog({
  userId,
  self,
  onClose,
}: {
  userId: string;
  self: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const { expire } = useSession();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      unwrap(
        await api().POST('/admin/users/{user_id}/password', {
          params: { path: { user_id: userId } },
          body: self
            ? { current_password: currentPassword, new_password: newPassword }
            : { new_password: newPassword },
        }),
      );
      onClose();
      // §5: a self password change revokes every session, including this one.
      if (self) expire();
    } catch (caught) {
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={self ? t('admin.users.changePassword') : t('admin.users.resetPassword')}
      onClose={onClose}
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button form="password-form" type="submit" disabled={busy}>
            {t('admin.common.save')}
          </Button>
        </>
      }
    >
      <form id="password-form" onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
        <ErrorBanner message={error} />
        <WarningBanner>{t('admin.users.passwordRevokesSessions')}</WarningBanner>
        {self ? (
          <Field label={t('admin.users.currentPassword')}>
            <TextInput
              type="password"
              autoComplete="current-password"
              required
              minLength={12}
              maxLength={256}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </Field>
        ) : null}
        <Field label={t('admin.users.newPassword')} hint={t('admin.users.passwordHint')}>
          <TextInput
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            maxLength={256}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </Field>
      </form>
    </Modal>
  );
}
